import { useEffect, useState } from "react";
import { Lock, ShieldCheck, Plus, Pencil, Trash2, Check, X, Clock, User, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const NOTE_CATS = [
  { key: "customer_review", label: "Customer Review", cls: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  { key: "customer_problem", label: "Customer Problem", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  { key: "complaint", label: "Complaint", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
  { key: "follow_up", label: "Follow-up", cls: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  { key: "important", label: "Important", cls: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  { key: "general", label: "General", cls: "bg-zinc-100 text-zinc-600", dot: "bg-zinc-400" },
];
const catOf = (k) => NOTE_CATS.find((c) => c.key === k) || NOTE_CATS[NOTE_CATS.length - 1];
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || ""; } };

export default function InvoiceInternalNotes({ invoiceId }) {
  const [notes, setNotes] = useState([]);
  const [category, setCategory] = useState("general");
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    if (!invoiceId) return;
    api.get(`/invoices/${invoiceId}/internal-notes`).then(({ data }) => setNotes(data || [])).catch(() => {}).finally(() => setLoaded(true));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceId]);

  const reset = () => { setEditingId(null); setContent(""); setCategory("general"); };

  const save = async () => {
    const body = { category, content: content.trim() };
    if (!body.content) { toast.error("Write a note before saving"); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/invoices/${invoiceId}/internal-notes/${editingId}`, body);
      else await api.post(`/invoices/${invoiceId}/internal-notes`, body);
      toast.success(editingId ? "Note updated" : "Internal note saved");
      reset(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save note"); }
    finally { setSaving(false); }
  };

  const startEdit = (n) => { setEditingId(n.id); setCategory(n.category); setContent(n.content); };
  const del = async (n) => {
    if (!window.confirm("Delete this internal note? This cannot be undone.")) return;
    try { await api.delete(`/invoices/${invoiceId}/internal-notes/${n.id}`); toast.success("Note deleted"); if (editingId === n.id) reset(); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!invoiceId) {
    return (
      <div data-testid="internal-notes-locked" className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-center">
        <EyeOff size={18} className="mx-auto text-zinc-400" />
        <p className="text-sm font-semibold text-zinc-600 mt-1.5">Save the invoice first</p>
        <p className="text-xs text-zinc-400 mt-0.5">Internal notes attach to a saved invoice.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="internal-notes-panel">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Lock size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading text-base font-bold text-zinc-900 leading-tight">Internal Customer Notes</h4>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><ShieldCheck size={11} />Private · Internal</span>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">Private notes for staff — customers cannot see this.</p>
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-4 space-y-3" data-testid="internal-notes-composer">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">Category</label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="internal-note-categories">
            {NOTE_CATS.map((c) => (
              <button key={c.key} type="button" data-testid={`internal-note-category-${c.key}`} onClick={() => setCategory(c.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition active:scale-95 ${category === c.key ? `${c.cls} border-transparent ring-2 ring-brand/30` : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}>
                <span className={`h-2 w-2 rounded-full ${c.dot}`} />{c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{editingId ? "Edit note" : "New note"}</label>
          <textarea data-testid="internal-note-textarea" value={content} onChange={(e) => setContent(e.target.value)} rows={4}
            placeholder="Write customer review, problem, complaint, follow-up, or any important internal note..."
            className="inp !h-auto py-3 mt-1 text-sm resize-y leading-relaxed" />
        </div>
        <div className="flex items-center justify-end gap-2">
          {editingId && <button type="button" data-testid="internal-note-cancel-edit" onClick={reset} className="h-10 px-4 rounded-xl border border-zinc-200 font-semibold text-sm text-zinc-600 hover:bg-zinc-50 active:scale-95 flex items-center gap-1.5"><X size={15} />Cancel</button>}
          <button type="button" data-testid="internal-note-save" onClick={save} disabled={saving || !content.trim()}
            className="h-10 px-5 rounded-xl bg-brand text-white font-semibold text-sm hover:brightness-110 active:scale-95 disabled:opacity-50 flex items-center gap-1.5">
            {editingId ? <Check size={16} /> : <Plus size={16} />}{saving ? "Saving…" : (editingId ? "Update note" : "Save note")}
          </button>
        </div>
      </div>

      {/* Saved notes */}
      <div className="space-y-2.5" data-testid="internal-notes-list">
        {loaded && notes.length === 0 && (
          <p className="text-sm text-zinc-400 text-center py-3" data-testid="internal-notes-empty">No internal notes yet. Add the first one above.</p>
        )}
        {notes.map((n) => {
          const c = catOf(n.category);
          const edited = n.updated_at && n.updated_at !== n.created_at;
          return (
            <div key={n.id} data-testid={`internal-note-item-${n.id}`} className="rounded-xl border border-zinc-200 bg-white p-3.5 shadow-sm transition hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${c.cls}`}><span className={`h-2 w-2 rounded-full ${c.dot}`} />{c.label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" title="Edit note" data-testid={`internal-note-edit-${n.id}`} onClick={() => startEdit(n)} className="h-8 w-8 rounded-lg border border-zinc-200 text-zinc-600 flex items-center justify-center hover:bg-zinc-50 active:scale-90"><Pencil size={14} /></button>
                  <button type="button" title="Delete note" data-testid={`internal-note-delete-${n.id}`} onClick={() => del(n)} className="h-8 w-8 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50 active:scale-90"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words mt-2.5 leading-relaxed">{n.content}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-2.5 border-t border-zinc-100 text-[11px] text-zinc-400">
                <span className="flex items-center gap-1"><User size={12} />{n.updated_by || n.created_by || "Staff"}</span>
                <span className="flex items-center gap-1"><Clock size={12} />{fmt(n.updated_at || n.created_at)}{edited ? " · edited" : ""}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
